/**
 * @fileoverview transactionBuilder - Build updated transaction objects from edit form.
 *
 * Converts edit form state into properly formatted transaction objects for API updates.
 * Similar to addTransactionBuilder but works with edit form state structure.
 *
 * @description This builder:
 * - Preserves transaction ID for updates
 * - Applies same transaction type rules as add builder
 * - Handles user category insertion for expenses
 * - Maintains usedTemplate flag for income transactions
 *
 * The logic mirrors addTransactionBuilder to ensure consistency between
 * add and edit operations.
 *
 * @module hooks/helpers/transactionBuilder
 */
import { getBudgetMonth } from '../../utils/dateUtils';

/**
 * Builds an updated transaction object from edit form data.
 *
 * @param {Object} editState - Edit form state from useTransactionEdit
 * @param {Array<string>} userCategories - User category names
 * @param {Array<Object>} users - Array of user objects with userKey and userName
 * @returns {Object} Transaction object ready for API update
 *
 * @example
 * const updatedTransaction = buildTransactionFromEdit(editState, userCategories, users);
 * // Returns: { id, amount, description, categories, date, type, budgetMonth, ... }
 */
export const buildTransactionFromEdit = (editState, userCategories, users = []) => {
    const {
        editingId,
        editVersion,  // Version for optimistic locking
        editTransactionType,
        editAmount,
        editDescription,
        editAdjustmentFromCategory,
        editAdjustmentToCategory,
        editDate,
        editCurrentUserKey,
        editPaidDirectly,
        editUseTemplateForIncome,
        editTransactionCategories
    } = editState;

    // Look up the user's category name from their userKey
    const editCurrentUser = users.find(u => u.userKey === editCurrentUserKey)?.userName || null;

    if (editTransactionType === 'Adjustment') {
        return {
            id: editingId,
            version: editVersion,  // Include version for optimistic locking
            amount: parseFloat(editAmount),
            description: editDescription.trim(),
            categories: [
                { category: editAdjustmentFromCategory, amount: -parseFloat(editAmount) },
                { category: editAdjustmentToCategory, amount: parseFloat(editAmount) }
            ],
            adjustmentFromCategory: editAdjustmentFromCategory,
            adjustmentToCategory: editAdjustmentToCategory,
            date: editDate,
            type: editTransactionType,
            budgetMonth: getBudgetMonth(editDate),
            paidByUserKey: editCurrentUserKey,
            paidDirectly: false,
            usedTemplate: false
        };
    } else {
        const transactionAmount = editTransactionType === 'Income'
            ? parseFloat(editAmount)
            : -parseFloat(editAmount);
        const editTransactionCategories_withUser = [...editTransactionCategories];

        if (editTransactionType === 'Expense' && !editPaidDirectly && editCurrentUser) {
            editTransactionCategories_withUser.push({
                category: editCurrentUser,
                amount: parseFloat(editAmount).toFixed(2)
            });
        }

        return {
            id: editingId,
            version: editVersion,  // Include version for optimistic locking
            amount: transactionAmount,
            description: editDescription.trim(),
            categories: editTransactionCategories_withUser.map(cat => {
                const categoryAmount = editTransactionType === 'Transfer'
                    ? parseFloat(editAmount)
                    : parseFloat(cat.amount);

                if (userCategories.includes(cat.category)) {
                    if (editTransactionType === 'Transfer') {
                        return { category: cat.category, amount: -categoryAmount };
                    } else {
                        return { category: cat.category, amount: categoryAmount };
                    }
                }

                return {
                    category: cat.category,
                    amount: editTransactionType === 'Income' ? categoryAmount : -categoryAmount
                };
            }),
            date: editDate,
            type: editTransactionType,
            budgetMonth: getBudgetMonth(editDate),
            usedTemplate: editTransactionType === 'Income' ? editUseTemplateForIncome : false,
            paidByUserKey: editCurrentUserKey,
            paidDirectly: editTransactionType === 'Expense' ? editPaidDirectly : false
        };
    }
};
